import AirAdvancedRulesValidator from 'event-widgets/lib/AirRequest/AirAdvancedRulesValidator';
import { getIn } from 'icepick';
import moment from 'moment';
import { isEmpty } from 'lodash';
import { utcToDisplayDate } from 'event-widgets/redux/modules/timezones';
import HotelAdvancedRulesValidator from 'event-widgets/lib/HotelRequest/HotelAdvancedRulesValidator';

// selectors
import { getAssociatedRegistrationPathId, getRegCart } from '../../redux/selectors/shared';
import { getAirRequestSnapshot, getHotelRegRules } from 'event-widgets/redux/selectors/eventTravel';
import { getTravelBookingsForCurrentRegistrantAndGuests } from '../../redux/travelCart/selectors';
import { getEventRegistration } from '../../redux/registrationForm/regCart/selectors';
import { getEventRegistrationId } from '../../redux/selectors/currentRegistrant';

const ALLOWED_ATTENDEE_TYPES = {
  INVITEE: 'invitee',
  INVITEE_AND_GUEST: 'inviteeAndGuest'
};

/**
 * validates if there is invalid hotel room booking in travel cart based on new reg path
 * @param state
 * @param registrationTypeId
 * @returns {{isValid: *, invalidTravelBookings: Array}}
 */
export function validateHotelBookings(state, registrationTypeId) {
  const bookings = getIn(state, ['travelCart', 'cart', 'bookings']);
  const hotelsData = getIn(state, ['eventTravel', 'hotelsData']);
  const newRegPathId = getAssociatedRegistrationPathId(state, registrationTypeId);
  let invalidTravelBookings = [];
  if (bookings && bookings.length > 0) {
    let hotelRoomsForRegPath = [];
    // get hotel rooms available for new reg path
    if (hotelsData && hotelsData.hotels && hotelsData.hotels.length > 0) {
      hotelsData.hotels.forEach(
        hotel => {
          hotelRoomsForRegPath.push(...hotel.roomTypes.filter(room => room.isOpenForRegistration &&
          room.associatedRegPathSettings && room.associatedRegPathSettings[newRegPathId]).map(room => room.id));
        }
      );
    }
    // find invalid room bookings based on hotel rooms available for new reg path
    bookings.forEach(booking => {
      booking.hotelRoomBookings.forEach(hotelRoomBooking => {
        if (!hotelRoomsForRegPath.includes(hotelRoomBooking.roomTypeId)) {
          invalidTravelBookings.push(hotelRoomBooking);
        }
      });
    });
  }
  return {
    isValid: isEmpty(invalidTravelBookings),
    invalidTravelBookings
  };
}

export function validateAirRequestAdvancedRules(state, newRegistrationTypeId, admissionItem) {
  if (!admissionItem) {
    return { isValid: true };
  }
  const airRequestSnapshot = getAirRequestSnapshot(state);
  const travelBookingsToValidate = getTravelBookingsForCurrentRegistrantAndGuests(state, getEventRegistrationId(state));
  const validator = new AirAdvancedRulesValidator(airRequestSnapshot, admissionItem.id, newRegistrationTypeId);
  return validator.getValidationResult(travelBookingsToValidate);
}

export function validateHotelBookingAdvancedRules(state, newRegistrationTypeId, admissionItem) {
  if (!admissionItem) {
    return { isValid: true };
  }
  const eventTimezone = getIn(state, ['timezones'])[getIn(state, ['event', 'timezone'])];
  const isPlanner = getIn(state, ['userSession', 'isPlanner']);
  const hotelRequestRules = getHotelRegRules(state);
  const travelBookingsToValidate = getTravelBookingsForCurrentRegistrantAndGuests(state, getEventRegistrationId(state));
  const hotels = getIn(state, ['eventTravel', 'hotelsData', 'hotels']);
  const validator = new HotelAdvancedRulesValidator(hotelRequestRules, admissionItem.id,
    newRegistrationTypeId, isPlanner, eventTimezone, hotels);
  return validator.getValidationResult(travelBookingsToValidate);
}

/**
 * validates if there is invalid air booking in travel cart based on new reg path
 * @param state
 * @param newRegistrationTypeId
 * @returns {{isValid: *, invalidTravelBookings: Array}}
 */
export function validateAirRequests(state, newRegistrationTypeId) {
  // get all travel cart bookings
  const bookings = getTravelBookingsForCurrentRegistrantAndGuests(state, getEventRegistrationId(state));
  // new path id based on new reg type id
  const newRegPathId = getAssociatedRegistrationPathId(state, newRegistrationTypeId);
  // get travel settings of new reg path
  const airRegSettings = getIn(getIn(state, ['appData', 'registrationSettings', 'registrationPaths'])[newRegPathId],
    ['travelSettings', 'airRequestSettings']);

  let invalidAirBookings = [];

  if (!isEmpty(bookings)) {
    const eventTimezone = getIn(state, ['timezones'])[getIn(state, ['event', 'timezone'])];
    const isPlanner = getIn(state, ['userSession', 'isPlanner']);
    // validate if air request is not dropped on reg path
    // validate air booking based on newRequestUntil
    if (!airRegSettings || (!isPlanner && airRegSettings.allowNewRequestUntil &&
      moment().isAfter(utcToDisplayDate(airRegSettings.allowNewRequestUntil, eventTimezone)))) {
      bookings.forEach(booking => invalidAirBookings.push(...booking.airBookings));
    } else {
      bookings.forEach(booking => {
        if (!isEmpty(booking.airBookings)) {
          // validate air booking based on maxNumberOfAirRequestsPerAttendee
          if (!isPlanner && airRegSettings.maxNumberOfAirRequestsPerAttendee &&
            airRegSettings.maxNumberOfAirRequestsPerAttendee < booking.airBookings.length) {
            invalidAirBookings.push(...booking.airBookings);
          } else {
            const eventRegistration = getEventRegistration(getRegCart(state), booking.id);
            booking.airBookings.forEach(airBooking => {
              if (// validate air booking based on allowedAttendeeTypes
                !hasValidAirBookingAttendeeType(airRegSettings.allowedAttendeeTypes, airBooking,
                  eventRegistration.attendeeType)) {
                invalidAirBookings.push(airBooking);
              }
            });
          }
        }
      });
    }
  }
  return {
    isValid: isEmpty(invalidAirBookings),
    invalidAirBookings
  };
}

/**
 * if passed air booking is made for an allowed attendee type
 * @param allowedAttendeeTypes
 * @param airBooking
 * @param attendeeType
 * @returns {boolean}
 */
function hasValidAirBookingAttendeeType(allowedAttendeeTypes, airBooking, attendeeType) {
  // if only invitee is allowed to make air bookings then air bookings for guest and others are invalid.
  if (allowedAttendeeTypes === ALLOWED_ATTENDEE_TYPES.INVITEE && (attendeeType === 'GUEST' ||
      (airBooking.isForOther && (attendeeType === 'GROUP_LEADER' || attendeeType === 'ATTENDEE')))) {
    return false;
  }
  return true;
}
